import { useEffect, useState } from 'react';
import { Modal, Spin, message } from 'antd';
import { CloseOutlined, DownloadOutlined } from '@ant-design/icons';
import { Button } from '@bancobarigui/barihaus';
import { inspectionReportService, InspectionReport } from '@/services/inspectionReport.service';
import { docmanService } from '@/services/docman.service';
import { InspectionRequest } from '@/services/types/inspection.types';
import { generatePdfBlobUrl } from '../pdf/utils/generatePdfOnTheFly';

interface ViewReportModalProps {
  open: boolean;
  report: InspectionReport | null;
  onClose: () => void;
  inspection?: InspectionRequest;
}

export function ViewReportModal({ open, report, onClose, inspection }: ViewReportModalProps) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const loadPdfUrl = async () => {
    if (!report) return;

    setLoading(true);
    try {
      // Se o report já tem documento no bucket, busca a presigned URL.
      // Caso contrário (laudo aguardando aprovação do banco), gera o PDF on-the-fly.
      if (report.documentId) {
        const urlData = await inspectionReportService.getReportDownloadUrl(report.id);
        const presignedUrl = await docmanService.getImageUrl(urlData.documentId);
        setPdfUrl(presignedUrl);
      } else {
        if (!inspection || !report.reportData) {
          message.error('Não foi possível gerar a visualização do laudo');
          return;
        }
        const reportDataParsed = JSON.parse(report.reportData as unknown as string);
        const wizardData = reportDataParsed.wizardData;
        const isAutomatic = reportDataParsed.isAutomaticReport === true;
        const blobUrl = await generatePdfBlobUrl({
          wizardData,
          inspection,
          isAutomatic,
        });
        setPdfUrl(blobUrl);
      }
    } catch {
      message.error('Erro ao carregar URL do laudo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && report) {
      loadPdfUrl();
    } else {
      setPdfUrl((prev) => {
        if (prev && prev.startsWith('blob:')) {
          URL.revokeObjectURL(prev);
        }
        return null;
      });
    }
  }, [open, report]);

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = report?.fileName || 'laudo.pdf';
      link.click();
    }
  };

  return (
    <Modal
      centered
      open={open}
      onCancel={onClose}
      width="90vw"
      style={{ maxWidth: 1400 }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button
            buttonColor="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            disabled={!pdfUrl}
          >
            Baixar PDF
          </Button>
          <Button onClick={onClose}>Fechar</Button>
        </div>
      }
      closeIcon={<CloseOutlined />}
    >
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Visualizar Laudo</h2>
        <p style={{ margin: 0, color: '#666', fontSize: 14 }}>
          {report?.fileName} {report?.fileSize ? `• ${(report.fileSize / 1024 / 1024).toFixed(2)} MB` : ''}
        </p>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 500 }}>
          <Spin size="large" tip="Carregando PDF..." />
        </div>
      ) : pdfUrl ? (
        <iframe
          src={`${pdfUrl}#toolbar=0`}
          style={{
            width: '100%',
            height: '75vh',
            border: '1px solid #e8e8e8',
            borderRadius: 4,
          }}
          title="Visualização do Laudo"
        />
      ) : (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
          <p>Nenhum PDF disponível para visualização</p>
        </div>
      )}
    </Modal>
  );
}
